import { FieldArray, Form, Formik } from "formik";
import moment from "moment/moment";
import { array, number, object, string } from "yup";
import { IconTrash } from "@tabler/icons-react";
import Text from "../UI/Text";
// @ts-expect-error pure js
import TextInput from "../UI/TextInput";
import FormikSubmitButton from "../UI/FormikSubmitButton";
import Button from "../UI/Button";
import { type Compteur } from "../../hooks/use-compteur";
import { CompteurType } from "../../models/compteur";
import styles from "./CompteurEditor.module.css";
// @ts-expect-error pure js
import { dateMax } from "../../models/utils";
import IconButton from "../UI/IconButton.tsx";
import ModalDialog, { ModalDialogFooter } from "../UI/ModalDialog";

export type EditableCompteur = Omit<Compteur, "id"> & { id?: number };

interface CompteurEditorValues {
  numero_compteur: string;
  compteurs: EditableCompteur[];
  deletedIds: number[];
}

interface CompteurEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  compteurType: CompteurType | null;
  compteurs: Compteur[];
  onSave: (
    compteurs: EditableCompteur[],
    deletedIds: number[],
  ) => Promise<void>;
}

const validationSchema = object({
  numero_compteur: string(),
  compteurs: array().of(
    object({
      date_releve: dateMax().required("Date requise"),
      valeur: number()
        .typeError("Valeur invalide")
        .min(0, "Valeur positive requise")
        .required("Valeur requise"),
    }),
  ),
});

export default function CompteurEditor({
  open,
  onOpenChange,
  compteurType,
  compteurs,
  onSave,
}: CompteurEditorProps) {
  const initialValues: CompteurEditorValues = {
    numero_compteur: compteurs[0]?.numero_compteur ?? "",
    compteurs: compteurs.map((c) => ({ ...c })),
    deletedIds: [],
  };

  async function handleSubmit(values: CompteurEditorValues) {
    const compteursWithNumero = values.compteurs.map((c) => ({
      ...c,
      numero_compteur: values.numero_compteur,
    }));
    await onSave(compteursWithNumero, values.deletedIds);
  }

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, resetForm }) => (
        <ModalDialog
          open={open}
          header={compteurType?.description}
          onOpenChange={(next) => {
            if (!next) resetForm();
            onOpenChange(next);
          }}
        >
          {compteurType && (
            <Form className={styles.form}>
              <TextInput
                label="N° de compteur"
                name="numero_compteur"
                placeholder="P01234JU"
                marginForm=""
              />

              <FieldArray name="compteurs">
                {(arrayHelpers) => (
                  <div className={styles.list}>
                    <Button
                      type="button"
                      onClick={() =>
                        arrayHelpers.unshift({
                          type: compteurType.name,
                          date_releve: moment().format("YYYY-MM-DD"),
                          valeur: "",
                        })
                      }
                    >
                      Ajouter un relevé
                    </Button>
                    <div className={styles.listHeader}>
                      <Text as="span" variant="deci" weight="medium">
                        Date
                      </Text>
                      <Text as="span" variant="deci" weight="medium">
                        Index ({compteurType.unite})
                      </Text>
                      <span />
                    </div>

                    {values.compteurs.map((compteur, index) => (
                      <div key={index} className={styles.row}>
                        <TextInput
                          name={`compteurs[${index}].date_releve`}
                          type="date"
                          marginForm=""
                        />
                        <TextInput
                          name={`compteurs[${index}].valeur`}
                          type="number"
                          step="0.01"
                          marginForm=""
                        />
                        <IconButton
                          label="Supprimer le relevé"
                          icon={IconTrash}
                          color="danger"
                          onClick={() => {
                            if (compteur.id !== undefined) {
                              setFieldValue("deletedIds", [
                                ...values.deletedIds,
                                compteur.id,
                              ]);
                            }
                            arrayHelpers.remove(index);
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </FieldArray>

              <ModalDialogFooter>
                <FormikSubmitButton>Enregistrer</FormikSubmitButton>
              </ModalDialogFooter>
            </Form>
          )}
        </ModalDialog>
      )}
    </Formik>
  );
}
